import { FC, useCallback, useEffect, useRef, useState } from 'react';

interface ITextInputProps {
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  invalid?: boolean;
  width?: string;
  height?: string;
  bgColor?: string;
  borderColor?: string;
  decimalFormattingMode?: boolean;
  disabled?: boolean;
}

export const TextInput: FC<ITextInputProps> = ({
  value,
  onChange,
  placeholder = '',
  invalid = false,
  height = '33px',
  width = '150px',
  bgColor = '#141316',
  borderColor = '#2B292F',
  decimalFormattingMode = false,
  disabled = false,
}) => {
  const [inputFocus, setInputFocus] = useState(false);
  const textInput = useRef<HTMLInputElement>(null);

  const NBSP = '\u00A0';
  const SEP_RE = useRef(new RegExp(NBSP, 'g')).current;

  const normalize = useCallback(
    (val: string | number) => {
      let str = val === null || val === undefined ? '' : String(val);
      if (!str) return '';
      str = str.replace(SEP_RE, '').replace(/[^\d.\-]/g, '');

      if (str.includes('-')) {
        str = (str.startsWith('-') ? '-' : '') + str.replace(/-/g, '');
      }

      const parts = str.split('.');
      if (parts.length > 2) str = parts.shift() + '.' + parts.join('');
      return str;
    },
    [SEP_RE]
  );

  const formatLoose = useCallback((raw: string) => {
    if (raw === '' || raw === '-') return raw;
    const sign = raw.startsWith('-') ? '-' : '';
    const [i = '', f] = (sign ? raw.slice(1) : raw).split('.');
    const gi = i.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
    return sign + (f !== undefined ? `${gi}.${f}` : gi);
  }, []);

  const formatFixed2 = useCallback((raw: string) => {
    if (!raw || raw === '-') return '';
    const n = Number(raw);
    if (!Number.isFinite(n)) return '';
    const [i, f] = n.toFixed(2).split('.');
    const gi = i.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
    return `${gi}.${f}`;
  }, []);

  const setCaretByRawIndex = useCallback(
    (el: HTMLInputElement, displayValue: string, rawLeft: number) => {
      let idx = 0,
        seen = 0;
      while (idx < displayValue.length && seen < rawLeft) {
        if (displayValue[idx] !== NBSP) seen++;
        idx++;
      }
      el.setSelectionRange(idx, idx);
    },
    [NBSP]
  );

  const onBlur = useCallback(() => {
    if (decimalFormattingMode) {
      const el = textInput.current;
      if (!el) return;
      const fixedDisplay = formatFixed2(normalize(el.value));
      if (fixedDisplay !== undefined) {
        el.value = fixedDisplay;
        onChange(normalize(fixedDisplay));
      }
    }
    if (value && value.length) return;
    setInputFocus(false);
  }, [decimalFormattingMode, formatFixed2, normalize, onChange, value]);

  useEffect(() => {
    if (!value) setInputFocus(false);
  }, [value]);

  const onClick = () => {
    setInputFocus(true);
    textInput?.current?.click();
    textInput?.current?.focus();
  };

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!decimalFormattingMode) {
        onChange(e.target.value);
        return;
      }

      const el = e.target;
      const prev = el.value;
      const sel = el.selectionStart ?? prev.length;

      const rawLeft = prev.slice(0, sel).replace(SEP_RE, '').length;

      const raw = normalize(prev);
      const display = formatLoose(raw);

      onChange(raw);

      requestAnimationFrame(() => {
        if (!textInput.current) return;
        textInput.current.value = display;
        setCaretByRawIndex(textInput.current, display, rawLeft);
      });
    },
    [decimalFormattingMode, formatLoose, normalize, onChange, SEP_RE, setCaretByRawIndex]
  );

  const displayedValue = decimalFormattingMode ? formatLoose(normalize(value)) : value;

  return (
    <div
      style={{
        minWidth: width,
        maxWidth: width,
        height,
        backgroundColor: disabled ? '#1a1a1a' : bgColor,
        borderColor: invalid ? '#E23336' : disabled ? '#2a2a2a' : borderColor,
        opacity: disabled ? 0.6 : 1,
      }}
      className={`h-[33px] w-full rounded-[8px] px-[10px] flex items-center border border-solid ${disabled ? 'cursor-not-allowed' : ''}`}
    >
      <input
        onBlur={onBlur}
        autoFocus={false}
        ref={textInput}
        value={displayedValue}
        disabled={disabled}
        style={{
          backgroundColor: disabled ? '#1a1a1a' : bgColor,
          color: disabled ? '#666666' : 'white',
        }}
        className={`outline-none ${inputFocus || value ? 'w-full h-full' : 'w-[0px] h-[0px]'} ${disabled ? 'cursor-not-allowed' : ''}`}
        onChange={disabled ? undefined : handleChange}
        inputMode={decimalFormattingMode ? 'decimal' : undefined}
        autoComplete="off"
      />

      <div
        onClick={disabled ? undefined : onClick}
        style={{
          backgroundColor: disabled ? '#1a1a1a' : bgColor,
          color: disabled ? '#444444' : '#777777',
        }}
        className={`${!inputFocus && (!value || !value?.length) ? 'w-full h-full flex items-center' : 'hidden'} ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        {placeholder}
      </div>
    </div>
  );
};
